import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import privateExamApi from "../../api/privateExamApi";
import QuestionImage from "../../components/Quiz/QuestionImage";
import QuestionCodeBlock from "../../components/Quiz/QuestionCodeBlock";
import "./PrivateExams.css";

const toIstText = (utcDate) =>
  new Intl.DateTimeFormat("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).format(new Date(utcDate));

const UserPrivateExamAttempt = () => {
  const { id } = useParams();
  const [exam, setExam] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [started, setStarted] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [submitResult, setSubmitResult] = useState(null);
  const [serverNow, setServerNow] = useState(Date.now());

  const fetchExam = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const res = await privateExamApi.getExamById(id);
      const data = res.data?.exam;
      setExam(data);
      setAnswers(Array(data.questions?.length || 0).fill(""));
      setStarted(data.status === "STARTED");
    } catch (err) {
      setError(err?.message || "Failed to load exam");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchExam();
  }, [id, fetchExam]);

  useEffect(() => {
    const timer = setInterval(() => setServerNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const countdownText = useMemo(() => {
    if (!exam) return "";
    const start = new Date(exam.startTime).getTime();
    const end = new Date(exam.endTime).getTime();
    if (serverNow < start) {
      const diff = start - serverNow;
      const mins = Math.floor(diff / 60000);
      const secs = Math.floor((diff % 60000) / 1000);
      return `Exam starts in ${mins}m ${secs}s`;
    }
    if (serverNow > end) return "Exam window closed.";
    const diff = end - serverNow;
    const mins = Math.floor(diff / 60000);
    const secs = Math.floor((diff % 60000) / 1000);
    return `Time left: ${mins}m ${secs}s`;
  }, [exam, serverNow]);

  const handleStart = async () => {
    try {
      setError("");
      setMessage("");
      await privateExamApi.startExam(id);
      setStarted(true);
      setMessage("Exam started.");
      fetchExam();
    } catch (err) {
      setError(err?.message || "Unable to start exam");
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      setError("");
      const res = await privateExamApi.submitExam(id, { answers });
      setSubmitResult(res.data);
      setStarted(false);
      setMessage("Exam submitted successfully.");
      fetchExam();
    } catch (err) {
      setError(err?.message || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="private-state-card">Loading exam...</div>;
  if (error && !exam) return <div className="private-state-card private-error">{error}</div>;
  if (!exam) return <div className="private-state-card">Exam not found.</div>;

  const isUpcoming = exam.computedState === "UPCOMING";
  const isCompletedWindow = exam.computedState === "COMPLETED";
  const alreadySubmitted = exam.status === "SUBMITTED";

  return (
    <section className="private-attempt-page">
      <header className="private-attempt-head">
        <h2>{exam.title}</h2>
        <p>{exam.description}</p>
        <div className="private-exam-meta">
          <span>Level: {exam.level}</span>
          <span>Time Limit: {exam.timeLimit} min</span>
          <span>Pass Marks: {exam.passMarks}</span>
          <span>Status: {exam.status}</span>
        </div>
        <div className="private-exam-time-window">
          <div>Start (IST): {toIstText(exam.startTime)}</div>
          <div>End (IST): {toIstText(exam.endTime)}</div>
        </div>
        <div className="private-countdown">{countdownText}</div>
      </header>

      {message ? <div className="private-state-card">{message}</div> : null}
      {error ? <div className="private-state-card private-error">{error}</div> : null}

      {submitResult ? (
        <div className="private-state-card">
          Score: {submitResult.score}/{submitResult.totalQuestions} |{" "}
          {submitResult.pass ? "PASS" : "FAIL"}
        </div>
      ) : null}

      {!alreadySubmitted && !started ? (
        <div className="actions-row">
          <button
            type="button"
            onClick={handleStart}
            disabled={isUpcoming || isCompletedWindow}
          >
            Start Exam
          </button>
        </div>
      ) : null}

      {alreadySubmitted ? (
        <div className="private-state-card">You have already submitted this exam.</div>
      ) : null}

      {started ? (
        <div className="private-question-list">
          {exam.questions.map((q, index) => (
            <div key={`private-question-${index}`} className="private-question-card">
              <h4>
                Q{index + 1}. {q.questionText}
              </h4>
              <QuestionImage src={q.imageUrl} alt={q.imageAlt || `Question ${index + 1}`} />
              <QuestionCodeBlock code={q.codeSnippet} language={q.codeLanguage} />
              <div className="private-options">
                {q.options.map((opt, optIdx) => (
                  <label key={`private-opt-${index}-${optIdx}`} className="private-option-item">
                    <input
                      type="radio"
                      name={`private-question-${index}`}
                      value={opt}
                      checked={answers[index] === opt}
                      onChange={(e) => {
                        const next = [...answers];
                        next[index] = e.target.value;
                        setAnswers(next);
                      }}
                    />
                    <span>{opt}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
          <div className="actions-row">
            <button type="button" onClick={handleSubmit} disabled={submitting}>
              {submitting ? "Submitting..." : "Submit Exam"}
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default UserPrivateExamAttempt;
